import { format, isToday, isTomorrow, differenceInCalendarDays } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import { User } from 'types'
import { StyledGridTypography } from '../../styles/elements/styleElements'
import { OrgRow, Row, TaskType } from './taskTypes'

export const DRAWER_WIDTH = { xs: 280, sm: 340, md: 400, lg: 460, xl: 820 } as const

const makeId = () =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
const nowIso = () => new Date().toISOString()

export const defaultOrgRow = (currentUser: User): OrgRow => ({
  org: null,
  name: '',
  platform: '',
  channel: '',
  taskType: '' as TaskType | '',
  taskTypeLabel: '',
  startDate: null,
  dateDue: null,
  addtlNotes: '',
  assigner: currentUser?.user_uuid ?? '',
  assignees: [],
  isOnboarding: false,
})

export const defaultRow = (currentUser: User): Row => ({
  org: null,
  platform: null,
  channel: null,
  deal_uuid: '',
  taskType: '',
  taskTypeLabel: '',
  startDate: null,
  endDate: null,
  dateDue: null,
  addtlNotes: '',
  assigner: currentUser?.user_uuid ?? '',
  assignees: [],
  queue: null,
  checklist: [],
  progress: { total: 0, done: 0 },
  selectedProduct: null,
  isOnboarding: false,
})
export const clonedOrgRow = (prev: OrgRow, currentUser: User): OrgRow => {
  return {
    org: prev?.org ?? null,
    platform: prev?.platform ?? null,
    name: prev?.name ?? '',
    channel: prev?.channel ?? null,
    taskType: prev?.taskType ?? '',
    taskTypeLabel: prev?.taskTypeLabel ?? '',
    startDate: prev?.startDate ?? null,
    dateDue: prev?.dateDue ?? null,
    addtlNotes: prev?.addtlNotes ?? '',
    assigner: prev?.assigner ?? currentUser?.user_uuid ?? '',
    assignees: Array.isArray(prev?.assignees) ? [...prev.assignees] : [],
    isOnboarding: false,
  }
}

export const clonedRow = (prev: Row, currentUser: User): Row => {
  // Deep-clone and regenerate checklist item IDs + timestamps
  const nextChecklist = Array.isArray(prev?.checklist)
    ? prev.checklist.map(item => ({
        ...item,
        id: makeId(),
        // keep label/metadata, reset state-y bits as you like:
        done: false,
        createdTime: nowIso(),
      }))
    : []

  const nextProgress = nextChecklist.length ? { total: nextChecklist.length, done: 0 } : { total: 0, done: 0 }

  return {
    // copy everything that’s user-facing
    org: prev?.org ?? null,
    platform: prev?.platform ?? null,
    channel: prev?.channel ?? null,
    deal_uuid: prev?.deal_uuid ?? '',
    taskType: prev?.taskType ?? '',
    taskTypeLabel: prev?.taskTypeLabel ?? '',
    startDate: prev?.startDate ?? null,
    endDate: prev?.endDate ?? null,
    dateDue: prev?.dateDue ?? null,
    addtlNotes: prev?.addtlNotes ?? '',
    assigner: prev?.assigner ?? currentUser?.user_uuid ?? '',
    assignees: Array.isArray(prev?.assignees) ? [...prev.assignees] : [],
    queue: prev?.queue ?? null,
    selectedProduct: prev?.selectedProduct ?? null,

    // regenerated
    checklist: nextChecklist,
    progress: nextProgress,
  }
}

export const calculateTaskDays = taskType => {
  const taskDaysMapping = {
    budget: 4,
    update: 4,
    updates: 4,
    review: 5,
    createSetup: 6,
    integration: 5,
    'Copy Update': 3,
    'Campaign Audit': 5,
    'Budget Rec': 4,
    'Budget Updates': 4,
    'Launch New Creatives': 5,
    'GTM/GA Setup': 4,
    'Campaign Setup': 6,
    'AM Task / Update': 4,
    'Offboard Client': 3,
    Discovery: 5,
    'Campaign Updates': 4,
    'Campaign Checklists': 4,
    'SEO Change': 4,
    'SEO Update': 3,
    'New Client Onboard': 6,
    Updates: 4,
    Budget: 4,
    'Create / Setup': 5,
    'Review / Audit': 5,
    'Social Ads': 3,
    'Display Ads': 3,
    'Ad Approvals': 3,
    socialAds: 3,
    displayAds: 3,
    organicSocial: 3,
    photography: 14,
    printDesign: 14,
    designMaterials: 14,
    website: 14,
    websiteDesignOverview: 14,
    websiteStructure: 14,
    video: 14,
    fullBrandBuild: 14,
    logoDesign: 14,
    marketingCollateral: 14,
  }
  return taskDaysMapping[taskType] || 0 // Default to 0 if task type not found
}
export const calculateDateDue = (taskType: string, startDate: Dayjs | null) => {
  const slaDays = taskType ? calculateTaskDays(taskType) : 0
  return addBusinessDays(startDate, slaDays)
}
export const addBusinessDays = (start: Dayjs | null | undefined, days: number): Dayjs | null => {
  if (!start || !dayjs.isDayjs(start)) return null
  if (!Number.isFinite(days) || days === 0) return start

  let d = start

  // If start falls on a weekend, roll forward to Monday before counting.
  while (d.day() === 0 || d.day() === 6) d = d.add(1, 'day')

  const step = days > 0 ? 1 : -1
  let remaining = Math.abs(days)

  while (remaining > 0) {
    d = d.add(step, 'day')
    const isWeekend = d.day() === 0 || d.day() === 6
    if (!isWeekend) remaining -= 1
  }
  return d
}
export const workday = (startDate, taskDays) => {
  if (!startDate) return
  const resultDate = new Date(startDate)
  resultDate.setDate(resultDate.getDate() + taskDays)
  return resultDate
}
export const formatDateString = date => {
  return format(new Date(date), 'MM/dd/yyyy') // Use 'MM/dd/yyyy' to ensure two digits
}

export const calculateDueDateDisplay = params => {
  const { masterStatus, highPrio, startDate, type, doneDate } = params.row

  if (masterStatus === 'Done' && doneDate) {
    return <StyledGridTypography sx={{ color: 'lightgreen' }}>{`Completed`}</StyledGridTypography>
  } else if (masterStatus === 'Done') {
    return 'Completed'
  }

  if (highPrio) {
    return <StyledGridTypography sx={{ color: 'crimson' }}>{`High Priority`}</StyledGridTypography>
  }

  // Calculate the task days and due date based on the task type and start date
  const taskDays = calculateTaskDays(type)
  const calculatedDueDate = workday(startDate, taskDays)

  // Now we use the calculatedDueDate in place of dueDate
  const dueDateObj = new Date(calculatedDueDate)
  const today = new Date()
  const daysDiff = differenceInCalendarDays(dueDateObj, today)

  if (isToday(dueDateObj)) {
    return <StyledGridTypography sx={{ color: 'rgb(255, 68, 161)' }}>{'TODAY'}</StyledGridTypography>
  } else if (daysDiff < 0) {
    const daysLate = Math.abs(daysDiff)
    return (
      <StyledGridTypography sx={{ color: 'pink' }}>{`LATE - ${daysLate} day${
        daysLate > 1 ? 's' : ''
      } ago`}</StyledGridTypography>
    )
  } else if (isTomorrow(dueDateObj)) {
    return 'Tomorrow'
  } else {
    const daysLeft = daysDiff
    return `${daysLeft} day${daysLeft > 1 ? 's' : ''} left`
  }
}
